import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import type { JcFilterGroup, JcFilterOption } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface ListResponse<T> {
  data: { total: number; data: T[] }
}

interface FilterGroupCreatePayload {
  id?: string
  technicalName: string
  selectionType: "single" | "multi"
  filterable?: boolean
  translations: Record<string, { name: string }>
}

interface FilterGroupUpdatePayload {
  technicalName?: string
  selectionType?: "single" | "multi"
  filterable?: boolean
  translations?: Record<string, { name: string }>
}

interface FilterOptionCreatePayload {
  id?: string
  eecomJcFilterGroupId: string
  technicalName: string
  mediaId?: string | null
  colorHexCode?: string | null
  position?: number
  translations: Record<string, { name: string }>
}

interface FilterOptionUpdatePayload {
  technicalName?: string
  mediaId?: string | null
  colorHexCode?: string | null
  position?: number
  translations?: Record<string, { name: string }>
}

interface JcFilterGroupsState {
  groups: JcFilterGroup[]
  loading: boolean
}

export const useJcFilterGroupsStore = defineStore({
  id: "jc.filter.groups",
  state: (): JcFilterGroupsState => ({
    groups: [],
    loading: false,
  }),
  actions: {
    async fetchGroups(): Promise<boolean> {
      this.loading = true
      try {
        const result = (await httpService.get(CONSTANT.API.JC_FILTER.FILTER_GROUP.LIST)) as ListResponse<JcFilterGroup>
        this.groups = result?.data?.data ?? []
        return true
      } catch (error) {
        console.error("Failed to fetch filter groups", error)
        this.groups = []
        return false
      } finally {
        this.loading = false
      }
    },

    async createGroup(payload: FilterGroupCreatePayload): Promise<string | null> {
      try {
        const result = (await httpService.post(CONSTANT.API.JC_FILTER.FILTER_GROUP.CREATE, payload)) as { data: { id: string } }
        await this.fetchGroups()
        return result?.data?.id ?? null
      } catch (error) {
        console.error("Failed to create filter group", error)
        return null
      }
    },

    async updateGroup(id: string, payload: FilterGroupUpdatePayload): Promise<boolean> {
      try {
        await httpService.patch(CONSTANT.API.JC_FILTER.FILTER_GROUP.UPDATE.replace("{id}", id), payload)
        await this.fetchGroups()
        return true
      } catch (error) {
        console.error("Failed to update filter group", error)
        return false
      }
    },

    async deleteGroup(id: string): Promise<boolean> {
      try {
        await httpService.delete(CONSTANT.API.JC_FILTER.FILTER_GROUP.DELETE.replace("{id}", id))
        await this.fetchGroups()
        return true
      } catch (error) {
        console.error("Failed to delete filter group", error)
        return false
      }
    },

    async fetchOptions(filterGroupId?: string): Promise<JcFilterOption[]> {
      try {
        const result = (await httpService.get(CONSTANT.API.JC_FILTER.FILTER_OPTION.LIST, filterGroupId ? { filterGroupId } : undefined)) as ListResponse<JcFilterOption>
        return result?.data?.data ?? []
      } catch (error) {
        console.error("Failed to fetch filter options", error)
        return []
      }
    },

    async createOption(payload: FilterOptionCreatePayload): Promise<string | null> {
      try {
        const result = (await httpService.post(CONSTANT.API.JC_FILTER.FILTER_OPTION.CREATE, payload)) as { data: { id: string } }
        return result?.data?.id ?? null
      } catch (error) {
        console.error("Failed to create filter option", error)
        return null
      }
    },

    async updateOption(id: string, payload: FilterOptionUpdatePayload): Promise<boolean> {
      try {
        await httpService.patch(CONSTANT.API.JC_FILTER.FILTER_OPTION.UPDATE.replace("{id}", id), payload)
        return true
      } catch (error) {
        console.error("Failed to update filter option", error)
        return false
      }
    },

    async deleteOption(id: string): Promise<boolean> {
      try {
        await httpService.delete(CONSTANT.API.JC_FILTER.FILTER_OPTION.DELETE.replace("{id}", id))
        return true
      } catch (error) {
        console.error("Failed to delete filter option", error)
        return false
      }
    },
  },
})
